// China eSIM landing page — the paid-search destination for the Google Ads
// campaign. Built from the 2026-07-31 design handoff (two locked breakpoints,
// 1280 desktop / 390 mobile).
//
// Mounted at /destinations/cn?preview=1 while it's being reviewed; the live
// China page is untouched until the flag in DestinationDetail's dispatcher is
// flipped. See that file for the mount logic.
//
// Copy lives in the `cnlp` i18n namespace (all eight locales). Nothing on this
// page is hardcoded prose except the app tile brand names, which are proper
// nouns and stay untranslated.

import { useEffect, useMemo, useState } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { CompatibilityModal } from '../components/CompatibilityModal'
import { useCatalog } from '../hooks/useCatalog'
import { track } from '../api/track'
import { gtagConversion } from '../api/gtag'
import { oaiqPlanClicked } from '../api/oaiq'
import { isUnlimited, plansForCountry, priceDollars, type Plan } from '../data/catalog'
import { languageFromPath, withLang } from '../i18n/i18n'
import { SITE_ORIGIN, useDocumentMeta } from '../i18n/useDocumentMeta'
import { useStructuredData } from '../i18n/useStructuredData'
import { notePlanClick, useCheckoutWarmup, warmOnIntent } from '../lib/checkoutWarmup'
import '../styles/china-lp.css'

interface AppTile {
  name: string
  bg: string
  ink: string
  mark?: string
}

// Tint/ink pairs are from the handoff. The first four use generic line glyphs
// rather than the official brand marks: this is a commercial page, and showing
// Google's/Meta's marks to imply their services work edges into implied
// endorsement, which their brand guidelines restrict. The ChatGPT and Claude
// marks shipped in the handoff bundle and are used as supplied.
const APPS: AppTile[] = [
  { name: 'Google', bg: '#E6EDFB', ink: '#2B5FCB' },
  { name: 'Instagram', bg: '#FBE7EE', ink: '#BE3A6E' },
  { name: 'WhatsApp', bg: '#E2F3E9', ink: '#17875A' },
  { name: 'YouTube', bg: '#EFE9FA', ink: '#6244B8' },
  { name: 'ChatGPT', bg: '#E2F1ED', ink: '#127962', mark: '/img/chatgpt-mark.webp' },
  { name: 'Claude', bg: '#FAE9DE', ink: '#B85A22', mark: '/img/claude-mark.webp' },
]

/** Renders **bold** runs in a copy string. */
function richText(s: string) {
  return s.split('**').map((chunk, i) =>
    i % 2 === 1 ? (
      <strong key={i} className="cnlp-em">
        {chunk}
      </strong>
    ) : (
      <span key={i}>{chunk}</span>
    ),
  )
}

/** Live viewport test — the FAQ is a real accordion below 900px and four
 *  always-open cards above it, which is a behaviour change CSS can't express. */
function useIsNarrow(): boolean {
  const query = '(max-width: 900px)'
  const [narrow, setNarrow] = useState(
    () => typeof window !== 'undefined' && window.matchMedia(query).matches,
  )
  useEffect(() => {
    const mq = window.matchMedia(query)
    const onChange = (e: MediaQueryListEvent) => setNarrow(e.matches)
    mq.addEventListener('change', onChange)
    setNarrow(mq.matches)
    return () => mq.removeEventListener('change', onChange)
  }, [])
  return narrow
}

interface Faq { q: string; a: string }

// The "Will it work on my phone?" answer carries the compatibility-checker
// button. Keyed by position rather than a flag in the JSON so the eight locale
// files can't drift on it — the question order is fixed and identical in every
// file, and new questions are APPEND-ONLY so this index never moves.
const COMPAT_FAQ_INDEX = 2
interface Step { title: string; body: string; bodyWide: string }

export default function ChinaLanding() {
  const { t } = useTranslation('cnlp')
  const location = useLocation()
  const lang = languageFromPath(location.pathname)
  const { plans, loading } = useCatalog()
  const narrow = useIsNarrow()
  const [openFaq, setOpenFaq] = useState<number | null>(0)
  const [compatOpen, setCompatOpen] = useState(false)

  // returnObjects arrays — one place each so the JSX below stays readable.
  const faqs = t('answers.faqs', { returnObjects: true }) as Faq[]
  const steps = t('how.steps', { returnObjects: true }) as Step[]
  const guarantees = t(narrow ? 'guaranteesShort' : 'guarantees', {
    returnObjects: true,
  }) as string[]

  const cnPlans = useMemo(() => (plans ? plansForCountry(plans, 'CN') : []), [plans])

  // Ladder: one group per trip length, ascending; sizes ascending within a
  // group, so the 999 GB "Unlimited" listing naturally lands last.
  const ladder = useMemo(() => {
    const byDays = new Map<number, Plan[]>()
    for (const p of cnPlans) {
      const g = byDays.get(p.validity_days)
      if (g) g.push(p)
      else byDays.set(p.validity_days, [p])
    }
    return [...byDays.entries()]
      .sort((a, b) => a[0] - b[0])
      .map(([days, rows]) => ({ days, rows: [...rows].sort((a, b) => a.data_gb - b.data_gb) }))
  }, [cnPlans])

  // Derived, never hardcoded — the CTA can't drift from the ladder it scrolls to.
  const planCount = cnPlans.length

  // Get checkout's payment form loading before the click (lib/checkoutWarmup.ts):
  // a few seconds after this page has loaded, once idle (not on low-memory
  // phones), or as soon as a plan row is hovered, pressed or focused.
  useCheckoutWarmup(planCount ? Math.min(...cnPlans.map((p) => p.price_cents)) : undefined)

  useDocumentMeta({ title: t('seo.title'), description: t('seo.description') })

  useEffect(() => {
    // Same event and shape the standard destination page fires, so this page
    // stays comparable to the one it replaces in the existing funnels. The
    // extra `variant` is additive.
    track('destination_view', { country: 'CN', name: 'China', variant: 'cn-lp' })
  }, [])

  const jsonLd = useMemo(() => {
    const url = `${SITE_ORIGIN}${withLang('/destinations/cn', lang)}`
    const graph: Record<string, unknown>[] = [
      {
        '@type': 'FAQPage',
        mainEntity: faqs.map((f) => ({
          '@type': 'Question',
          name: f.q,
          acceptedAnswer: { '@type': 'Answer', text: f.a.replace(/\*\*/g, '') },
        })),
      },
      {
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: 'Home', item: `${SITE_ORIGIN}${withLang('/', lang)}` },
          {
            '@type': 'ListItem',
            position: 2,
            name: 'Destinations',
            item: `${SITE_ORIGIN}${withLang('/destinations', lang)}`,
          },
          { '@type': 'ListItem', position: 3, name: 'China', item: url },
        ],
      },
    ]
    if (cnPlans.length > 0) {
      graph.unshift({
        '@type': 'Product',
        name: 'China eSIM',
        description: 'Prepaid travel eSIM for mainland China on China Mobile 5G.',
        image: `${SITE_ORIGIN}/img/cn-hero.webp`,
        category: 'Travel eSIM',
        brand: { '@id': `${SITE_ORIGIN}/#organization` },
        offers: {
          '@type': 'AggregateOffer',
          priceCurrency: 'USD',
          lowPrice: priceDollars(Math.min(...cnPlans.map((p) => p.price_cents))),
          offerCount: cnPlans.length,
          availability: 'https://schema.org/InStock',
          url,
          seller: { '@id': `${SITE_ORIGIN}/#organization` },
        },
      })
    }
    return { '@context': 'https://schema.org', '@graph': graph }
  }, [cnPlans, lang, faqs])
  useStructuredData(jsonLd)

  const onPlanClick = (p: Plan) => {
    notePlanClick(p.id)
    track('plan_clicked', {
      plan_id: p.id,
      country: 'CN',
      price_cents: p.price_cents,
      validity_days: p.validity_days,
      data_gb: p.data_gb,
      variant: 'cn-lp',
    })
    gtagConversion('plan_clicked')
    oaiqPlanClicked(p.price_cents)
  }

  const onFaqToggle = (i: number) => {
    const opening = openFaq !== i
    setOpenFaq(opening ? i : null)
    if (opening) track('faq_opened', { page: 'cn-lp', question: faqs[i].q })
  }

  const openCompat = () => {
    setCompatOpen(true)
    track('compat_opened', { page: 'cn-lp' })
  }

  return (
    <div className="cnlp">
      {/* ---------------------------------------------------------- hero */}
      <section className="cnlp-hero">
        <div className="cnlp-hero-copy">
          <div className="cnlp-hero-head">
            <div className="cnlp-eyebrow">
              <FlagCN />
              <span>{t('eyebrow.product')}</span>
              <span className="cnlp-eyebrow-dot" aria-hidden="true" />
              <span>{t('eyebrow.scope')}</span>
            </div>
            <h1 className="cnlp-h1">{t('h1')}</h1>
            {/* One string with a **bold** run, so the space (or absence of one)
                before the emphasis belongs to the translator — Chinese and
                Japanese need none after 。, Latin locales do. */}
            <p className="cnlp-subhead">{richText(t('subhead'))}</p>
          </div>

          <div className="cnlp-tiles-wrap">
            <ul className="cnlp-tiles">
              {APPS.map((a) => (
                <li key={a.name} className="cnlp-tile">
                  <span className="cnlp-tile-sq" style={{ background: a.bg, color: a.ink }}>
                    {a.mark ? (
                      <img src={a.mark} alt="" width={31} height={31} aria-hidden="true" />
                    ) : (
                      <AppGlyph name={a.name} />
                    )}
                  </span>
                  <span className="cnlp-tile-label">{a.name}</span>
                </li>
              ))}
            </ul>
            <p className="cnlp-tiles-caption">
              <span className="cnlp-narrow-only">+ </span>
              {t('tilesCaption')}
            </p>
          </div>

          <div className="cnlp-action">
            <a
              className="cnlp-cta"
              href="#cnlp-plans"
              onClick={() => track('cta_clicked', { page: 'cn-lp', location: 'hero' })}
            >
              {planCount ? t('plans.cta', { count: planCount }) : t('plans.ctaPlain')}
            </a>
            <ul className="cnlp-guarantees">
              {guarantees.map((g) => (
                <li key={g}>
                  <CheckMark />
                  <span>{g}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>

        <figure className="cnlp-hero-media">
          <picture>
            <source media="(max-width: 900px)" srcSet="/img/cn-hero-wide.webp" />
            <img
              src="/img/cn-hero.webp"
              alt={t('photo.alt')}
              width={878}
              height={987}
              fetchPriority="high"
              decoding="async"
            />
          </picture>
          <div className="cnlp-hero-scrim" aria-hidden="true" />
          <figcaption className="cnlp-hero-caption">
            <span className="cnlp-hero-claim">
              <SignalArc />
              {t('photo.claim')}
            </span>
            <span className="cnlp-hero-sub">{t('photo.sub')}</span>
          </figcaption>
        </figure>
      </section>

      {/* --------------------------------------------------------- plans */}
      <section className="cnlp-plans" id="cnlp-plans">
        <div className="cnlp-plans-inner">
          <h2 className="cnlp-h2">{t('plans.heading')}</h2>
          <p className="cnlp-credit">
            <DollarSign />
            <span>{t('plans.credit')}</span>
          </p>

          {loading ? (
            // Skeleton, not the fallback below: the catalogue is fetched
            // client-side after an ~870 KB bundle, so there's a real window
            // where plans haven't arrived. Showing the "browse elsewhere" link
            // during it tells a visitor there are no China plans. Four groups
            // of four is close enough to the real 2/4/4/5 shape to keep the
            // layout from jumping when the rows land.
            <div className="cnlp-ladder" aria-hidden="true">
              {[0, 1, 2, 3].map((g) => (
                <div key={g} className="cnlp-group">
                  <span className="cnlp-skel cnlp-skel-label" />
                  <ul className="cnlp-rows">
                    {[0, 1, 2, 3].map((r) => (
                      <li key={r}>
                        <span className="cnlp-skel-row" />
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          ) : ladder.length === 0 ? (
            // Only reachable once the fetch has settled with nothing usable.
            // The CTA above scrolls here, so it must never be a dead section.
            <p className="cnlp-plans-fallback">
              <Link to={withLang('/destinations', lang)}>{t('plans.fallback')}</Link>
            </p>
          ) : (
            <div className="cnlp-ladder">
              {ladder.map((group) => (
                <div key={group.days} className="cnlp-group">
                  <h3 className="cnlp-group-label">{t('plans.groupLabel', { days: group.days })}</h3>
                  <ul className="cnlp-rows">
                    {group.rows.map((p) => (
                      <li key={p.id}>
                        <Link
                          className="cnlp-row"
                          to={withLang(`/checkout?plan=${encodeURIComponent(p.id)}`, lang)}
                          onClick={() => onPlanClick(p)}
                          {...warmOnIntent(p.price_cents)}
                        >
                          <span className="cnlp-row-size">
                            {isUnlimited(p) ? t('plans.unlimited') : `${p.data_gb} GB`}
                          </span>
                          <span className="cnlp-row-price">
                            ${priceDollars(p.price_cents)}
                            <span className="cnlp-row-usd">USD</span>
                          </span>
                          {/* Desktop ends the row with an arrow, mobile with a
                              chevron in a disc — two glyphs, one per breakpoint. */}
                          <span className="cnlp-row-go" aria-hidden="true">
                            <RowArrow />
                            <RowChevron />
                          </span>
                        </Link>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          )}

          <div className="cnlp-crosssell">
            <span>{t('plans.crossSellQ')}</span>
            <Link to={withLang('/destinations/chm', lang)}>{t('plans.crossSellA')}</Link>
          </div>
        </div>
      </section>

      {/* ------------------------------------------- answers + how it works */}
      <section className="cnlp-mid">
        <div className="cnlp-answers">
          <h2 className="cnlp-h2">{t('answers.heading')}</h2>
          <div className="cnlp-faqs">
            {faqs.map((f, i) => {
              const open = !narrow || openFaq === i
              return (
                <div key={f.q} className={`cnlp-faq${open ? ' is-open' : ''}`}>
                  {narrow ? (
                    <h3 className="cnlp-faq-h">
                      <button
                        type="button"
                        className="cnlp-faq-toggle"
                        aria-expanded={open}
                        aria-controls={`cnlp-faq-${i}`}
                        onClick={() => onFaqToggle(i)}
                      >
                        <span>{f.q}</span>
                        <Chevron up={open} />
                      </button>
                    </h3>
                  ) : (
                    <h3 className="cnlp-faq-h cnlp-faq-h--static">{f.q}</h3>
                  )}
                  {open && (
                    <div className="cnlp-faq-a" id={`cnlp-faq-${i}`}>
                      <p>{richText(f.a)}</p>
                      {i === COMPAT_FAQ_INDEX && (
                        <button type="button" className="cnlp-compat" onClick={openCompat}>
                          {t('answers.compatCta')}
                        </button>
                      )}
                    </div>
                  )}
                </div>
              )
            })}
          </div>
        </div>

        <div className="cnlp-how">
          <h2 className="cnlp-h2">{t('how.heading')}</h2>
          <ol className="cnlp-steps">
            {steps.map((s, i) => (
              <li key={s.title}>
                <span className="cnlp-step-n" aria-hidden="true">
                  {i + 1}
                </span>
                <div>
                  <h3 className="cnlp-step-t">{s.title}</h3>
                  <p className="cnlp-step-b">
                    {narrow ? s.body : s.bodyWide}
                  </p>
                </div>
              </li>
            ))}
          </ol>
          <p className="cnlp-fineprint">{t('how.finePrint')}</p>
          {/* Contextual internal link to the blog article — English only (the
              piece is English; this is the highest-value indexed page to link
              it from for crawl + authority). Real followed <a>, not nofollow. */}
          {lang === 'en' && (
            <p className="cnlp-fineprint">
              New to travel eSIMs?{' '}
              <a href="https://blog.nimvoy.com/blog/unpack-travel-esim-business/">
                How to tell a good travel eSIM from a cheap one
              </a>
              {' '}— network, speed and the hidden costs.
            </p>
          )}
        </div>
      </section>

      {/* ------------------------------------------------------- closing */}
      <section className="cnlp-close">
        <div className="cnlp-close-copy">
          <h2 className="cnlp-close-h">{t('closing.h')}</h2>
          <p className="cnlp-close-promise">
            <ShieldCheck />
            <span>
              <strong>{t('closing.promiseLabel')}</strong>
              {t('closing.promise')}
            </span>
          </p>
        </div>
        <div className="cnlp-close-actions">
          <Link
            className="cnlp-cta cnlp-cta--light"
            to={withLang('/destinations/cn', lang)}
            onClick={(e) => {
              e.preventDefault()
              track('cta_clicked', { page: 'cn-lp', location: 'closing' })
              document.getElementById('cnlp-plans')?.scrollIntoView({ behavior: 'smooth' })
            }}
          >
            {t('closing.cta')}
          </Link>
          <Link className="cnlp-refund-link" to={withLang('/refund-policy', lang)}>
            {t('closing.refundLink')}
          </Link>
        </div>
      </section>

      <CompatibilityModal open={compatOpen} onClose={() => setCompatOpen(false)} />
    </div>
  )
}

/* ------------------------------------------------------------------ icons
   Local rather than the shared <Icon>: the design calls for stroke weights
   from 1.7 to 2.6 tuned per size, and Icon hardcodes 1.6 on every path. */

const S = {
  fill: 'none',
  stroke: 'currentColor',
  strokeLinecap: 'round' as const,
  strokeLinejoin: 'round' as const,
}

function AppGlyph({ name }: { name: string }) {
  const common = { viewBox: '0 0 24 24', width: '100%', height: '100%', ...S, strokeWidth: 1.7 }
  switch (name) {
    case 'Google':
      return (
        <svg {...common} aria-hidden="true">
          <path d="M12 3a9 9 0 1 0 9 9h-9" />
        </svg>
      )
    case 'Instagram':
      return (
        <svg {...common} aria-hidden="true">
          <rect x="3.5" y="3.5" width="17" height="17" rx="5" />
          <circle cx="12" cy="12" r="4" />
          <circle cx="16.9" cy="7.1" r="1.05" fill="currentColor" stroke="none" />
        </svg>
      )
    case 'WhatsApp':
      return (
        <svg {...common} aria-hidden="true">
          <path d="M20.5 11.7a8.5 8.5 0 0 1-12.6 7.45L3.5 20.5l1.4-4.3A8.5 8.5 0 1 1 20.5 11.7Z" />
          <path d="M9.2 9.1c.3 1.9 1.9 3.7 3.9 4.4l1-1.2 1.9.8" />
        </svg>
      )
    case 'YouTube':
      return (
        <svg {...common} aria-hidden="true">
          <rect x="2.5" y="5.5" width="19" height="13" rx="4" />
          <path d="M10.4 9.4 15.6 12l-5.2 2.6Z" />
        </svg>
      )
    default:
      return null
  }
}

function FlagCN() {
  return (
    <svg className="cnlp-flag" viewBox="0 0 17 11" aria-hidden="true">
      <rect width="17" height="11" rx="1.4" fill="#DE2910" />
      <g fill="#FFDE00">
        <polygon points="4.2,1.4 4.73,2.87 6.29,2.92 5.06,3.88 5.49,5.38 4.2,4.5 2.91,5.38 3.34,3.88 2.11,2.92 3.67,2.87" />
        <circle cx="8.2" cy="1.7" r="0.5" />
        <circle cx="9.7" cy="3.1" r="0.5" />
        <circle cx="9.5" cy="5.2" r="0.5" />
        <circle cx="8.0" cy="6.5" r="0.5" />
      </g>
    </svg>
  )
}

function CheckMark() {
  return (
    <svg className="cnlp-check" viewBox="0 0 12 12" {...S} strokeWidth={2.4} aria-hidden="true">
      <path d="M2 6.4 4.7 9.2 10 3.2" />
    </svg>
  )
}

function SignalArc() {
  return (
    <svg className="cnlp-arc" viewBox="0 0 24 24" {...S} strokeWidth={2} aria-hidden="true">
      <path d="M4.5 10.5a10.5 10.5 0 0 1 15 0" />
      <path d="M8 14a6 6 0 0 1 8 0" />
      <circle cx="12" cy="17.8" r="1.2" fill="currentColor" stroke="none" />
    </svg>
  )
}

function ShieldCheck() {
  return (
    <svg className="cnlp-shield" viewBox="0 0 24 24" {...S} strokeWidth={1.9} aria-hidden="true">
      <path d="M12 2.8 4.8 5.8v5.5c0 4.4 3 8.1 7.2 9.9 4.2-1.8 7.2-5.5 7.2-9.9V5.8Z" />
      <path d="m8.9 11.9 2.2 2.2 4.1-4.4" />
    </svg>
  )
}

function DollarSign() {
  return (
    <svg className="cnlp-dollar" viewBox="0 0 24 24" {...S} strokeWidth={1.9} aria-hidden="true">
      <path d="M12 3v18" />
      <path d="M16.2 7.3c-.7-1.4-2.2-2.2-4.2-2.2-2.4 0-4 1.2-4 3 0 4.3 8.4 2 8.4 6.3 0 1.9-1.8 3.1-4.4 3.1-2.2 0-3.8-.9-4.4-2.4" />
    </svg>
  )
}

function RowArrow() {
  return (
    <svg className="cnlp-wide-only" viewBox="0 0 15 15" {...S} strokeWidth={2.2} aria-hidden="true">
      <path d="M2.6 7.5h9.3M8.4 4l3.6 3.5-3.6 3.5" />
    </svg>
  )
}

function RowChevron() {
  return (
    <svg className="cnlp-narrow-only" viewBox="0 0 13 13" {...S} strokeWidth={2.2} aria-hidden="true">
      <path d="M5 2.6 9.4 6.5 5 10.4" />
    </svg>
  )
}

// Path swap, not a CSS rotate — a transform lagged behind the open/close in
// the designer's testing.
function Chevron({ up }: { up: boolean }) {
  return (
    <svg className="cnlp-chev" viewBox="0 0 24 24" {...S} strokeWidth={2.4} aria-hidden="true">
      <path d={up ? 'M5 14.5 12 8l7 6.5' : 'M5 9.5 12 16l7-6.5'} />
    </svg>
  )
}
